import React, { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  goHome() {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.hash = '';
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-rb-surface via-rb-elevated to-[#162d4e] flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-rb-surface rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-red-600 px-6 py-4">
              <h2 className="text-white font-bold text-lg">Application Error</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-red-400 font-medium">Something went wrong:</p>
              <div className="bg-red-500/10 border border-red-900/40 rounded-lg p-3 overflow-auto max-h-40">
                <pre className="text-xs text-red-400 font-mono whitespace-pre-wrap">
                  {this.state.error?.toString()}
                </pre>
              </div>
              {this.state.errorInfo && (
                <div className="p-3 overflow-auto max-h-40 rb-inner-card-sm">
                  <pre className="text-xs text-slate-600 font-mono whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
              {/* Primary: go home without data wipe */}
              <button
                onClick={() => this.goHome()}
                className="w-full py-3 rounded-xl bg-rb-active text-white font-bold hover:bg-rb-surface transition-colors"
              >
                ← Go to Home
              </button>
              {/* Secondary: nuke localStorage and go home */}
              <button
                onClick={() => {
                  localStorage.clear();
                  window.location.hash = '';
                  this.setState({ hasError: false, error: null, errorInfo: null });
                }}
                className="w-full py-3 rounded-xl bg-rb-elevated text-slate-400 font-bold hover:bg-rb-border transition-colors"
              >
                Clear Data & Go Home
              </button>
              <button
                onClick={() => { window.location.hash = '#/login'; }}
                className="w-full py-2 rounded-xl bg-transparent text-slate-400 text-sm hover:text-slate-600 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
